/**
 * cashfreeReconcile.js — PURE, testable Cashfree-only reconciliation logic.
 *
 * Cashfree is the ONLY supported gateway. There is NO Razorpay path here and no
 * gateway probing. All money is compared in integer paise (never floating rupees).
 *
 * The network-facing service (PaymentReconciliationService) fetches the Cashfree
 * order + payments via the proxy and hands the raw data to `verifyCashfreePayment`.
 * Keeping the decision pure makes every invariant unit-testable offline.
 */

// Deterministic result codes.
export const RECON = {
    PAID: 'PAID',
    FAILED: 'FAILED',
    PENDING: 'PENDING',
    ABANDONED: 'ABANDONED',            // ERP-internal: old unpaid order, no Cashfree record
    COD_SKIPPED: 'COD_SKIPPED',        // COD never enters prepaid verification
    UNSUPPORTED_GATEWAY: 'UNSUPPORTED_GATEWAY', // legacy Razorpay / unknown → manual review
    NEEDS_REVIEW: 'NEEDS_REVIEW',      // cannot safely mark PAID (bad total, non-INR, amount mismatch, …)
    ANOMALY: 'ANOMALY',                // conflicting successful payment on an already-paid order
    NOOP: 'NOOP',                      // idempotent replay — nothing to do
    NOT_FOUND: 'NOT_FOUND',            // Cashfree has no such order
    ACTIVE: 'ACTIVE',                  // Cashfree order EXISTS and is still awaiting payment
};

const STALE_MS = 15 * 60 * 1000;

/**
 * Exact money: rupees (number or string) → integer paise, or null if invalid.
 * Accepts only non-negative values with at most 2 decimals. No floating math.
 */
export function rupeesToPaise(v) {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
    const [intPart, fracRaw = ''] = s.split('.');
    const frac = (fracRaw + '00').slice(0, 2);
    return Number(intPart) * 100 + Number(frac);
}

/**
 * COD detection from the ACTUAL payment-type field (payment.mode), not the
 * absence of a gateway. COD classification always wins over any stale gateway/id.
 */
export function isCOD(order) {
    const mode = String(order?.payment?.mode ?? '').trim().toLowerCase();
    return mode === 'cod' || mode === 'cash' || mode === 'cashondelivery' || mode.includes('cash on delivery');
}

/**
 * Gateway resolution WITHOUT probing.
 *   COD                          → { type: 'cod' }
 *   explicit Cashfree            → { type: 'cashfree' }
 *   legacy Razorpay / unknown    → { type: 'unsupported', reason }
 * Never silently assumes Cashfree; never returns a Razorpay path.
 */
export function resolveGateway(order) {
    if (isCOD(order)) return { type: 'cod' };
    const p = order?.payment || {};
    const gw = String(p.gateway ?? '').trim().toLowerCase();
    const hasCashfreeId = !!(p.cashfree_order_id || p.cashfree_payment_id || p.cf_payment_id);
    const hasRazorpayId = !!(p.razorpay_payment_id || p.razorpay_order_id || p.rzp_status);

    if (gw === 'razorpay' || (hasRazorpayId && !hasCashfreeId && gw !== 'cashfree')) {
        return { type: 'unsupported', reason: 'razorpay-legacy' };
    }
    if (gw === 'cashfree' || hasCashfreeId) return { type: 'cashfree' };
    // Online order with NO gateway stamped. Our website creates prepaid orders without
    // setting payment.gateway / cashfree_order_id, which previously made every website
    // order "unsupported" and impossible to auto-verify. Cashfree is our ONLY gateway,
    // and verifyCashfreePayment is strict (Cashfree order_status PAID + a SUCCESS payment
    // + INR + paid amount EXACTLY equal to the stored total), so attempting the lookup can
    // never wrongly mark an order paid — at worst Cashfree has no such order and it simply
    // stays unverified.
    return { type: 'cashfree', assumed: true };
}

/**
 * The Cashfree order id to query: prefer an explicitly stored id, else the order
 * id. NO fuzzy/digits-only fallback — linkage must be exact.
 */
export function cashfreeOrderId(order) {
    const p = order?.payment || {};

    // The lookup key is the MERCHANT order_id we sent Cashfree — never their own
    // cf_order_id, which 404s. The storefront checkout stored cf_order_id in the
    // cashfree_order_id field, so a paid storefront order reported "not found" and its
    // money stayed invisible (verified against the live API: 'DQ20' returns PAID, while
    // its cf_order_id 7144498988 and the numeric fallback '20' both 404).
    if (p.cashfree_merchant_order_id) return String(p.cashfree_merchant_order_id);

    // The storefront sends the ERP order id itself as the merchant order_id.
    if (order?.source === 'storefront' && order?.id) return String(order.id);

    if (p.cashfree_order_id) return String(p.cashfree_order_id);
    // Our website sends Cashfree the NUMERIC part of the ERP id as the merchant order_id
    // ("DO002238" → "2238"; Cashfree's own "CF Order ID" 7070707572 is NOT the lookup key).
    // Derive it when the site didn't store an explicit id.
    const digits = String(order?.id || '').replace(/\D/g, '');
    if (digits) {
        const n = parseInt(digits, 10); // drops leading zeros: "002238" → 2238
        if (Number.isFinite(n) && n > 0) return String(n);
    }
    return order?.id || null;
}

/**
 * True only when the order carries a REAL Cashfree linkage (explicit id or gateway),
 * i.e. we are certain which Cashfree order this is. When false we are *inferring* the id,
 * so a "not found" means "unknown", never "abandoned".
 */
export function hasConfirmedCashfreeLink(order) {
    const p = order?.payment || {};

    // An explicit merchant order id, or an actual payment id, is proof.
    if (p.cashfree_merchant_order_id || p.cf_payment_id || p.cashfree_payment_id) return true;

    // A storefront order whose merchant id was never recorded is a GUESS: the checkout
    // used to store Cashfree's internal cf_order_id in the cashfree_order_id field, so
    // the id we would query with is inferred from the order id, and older orders may have
    // been sent under a different one. "Not found" against a guess means UNKNOWN, never
    // abandoned — falsely stamping ABANDONED on live orders has happened here before.
    if (order?.source === 'storefront') return false;

    return !!(p.cashfree_order_id || String(p.gateway ?? '').trim().toLowerCase() === 'cashfree');
}

/** Drop undefined values — Firebase rejects them outright. */
const compact = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== ''));

/**
 * AUDIT TRAIL: everything Cashfree tells us about HOW the money arrived, kept on the
 * order so an auditor can match an ERP order to a bank/UPI statement without logging
 * into the gateway. `bank_reference` is the UTR — the number that appears on the bank
 * statement — and `instrument` is the human-readable payer handle (UPI id, masked card,
 * bank name). Pure: no network, no clock.
 */
export function extractPaymentAudit(pay) {
    if (!pay) return null;
    const m = pay.payment_method || {};
    const group = String(pay.payment_group || Object.keys(m)[0] || '').toLowerCase();

    let instrument; let upiId;
    if (m.upi) { upiId = m.upi.upi_id || undefined; instrument = upiId; }
    else if (m.card) {
        const c = m.card;
        instrument = [c.card_network, c.card_bank_name, c.card_number ? `••••${String(c.card_number).slice(-4)}` : null].filter(Boolean).join(' ') || undefined;
    } else if (m.netbanking) instrument = m.netbanking.netbanking_bank_name || m.netbanking.netbanking_bank_code || undefined;
    else if (m.wallet) instrument = m.wallet.wallet_name || undefined;
    else if (m.app) instrument = m.app.provider || undefined;

    return compact({
        cf_payment_id: pay.cf_payment_id ? String(pay.cf_payment_id) : undefined,
        bank_reference: pay.bank_reference ? String(pay.bank_reference) : undefined, // UTR
        method: group || undefined,
        instrument,
        upi_id: upiId,
        payment_time: pay.payment_time || undefined,
        completion_time: pay.payment_completion_time || undefined,
        message: pay.payment_message || undefined,
        amount: pay.payment_amount ?? undefined,
        currency: pay.payment_currency || undefined,
        captured: pay.is_captured === true ? true : undefined,
        auth_id: pay.auth_id || undefined,
    });
}

/**
 * Pure verification. Inputs are the ERP order plus the raw Cashfree
 * order object and payments array (as returned by /pg/orders/{id} and
 * /pg/orders/{id}/payments). Returns { result, reason, updates?, cfPaymentId? }.
 *
 * To mark PAID, ALL of these must hold:
 *   - not COD
 *   - stored expected final total present, well-formed, positive
 *   - Cashfree order exists and order_status === 'PAID'
 *   - a payment attempt with payment_status === 'SUCCESS' and a valid cf_payment_id
 *   - currency exactly 'INR'
 *   - paid amount (paise) EXACTLY equals the stored expected total (paise)
 *   - no conflicting prior successful cf_payment_id
 */
export function verifyCashfreePayment({ order, cashfreeOrder, payments, cfId = null, now = Date.now() }) {
    if (isCOD(order)) return { result: RECON.COD_SKIPPED, reason: 'order is COD' };

    const p = order?.payment || {};
    const expectedPaise = rupeesToPaise(p.total);
    if (expectedPaise === null || expectedPaise <= 0) {
        return { result: RECON.NEEDS_REVIEW, reason: 'expected total absent/malformed/non-positive' };
    }

    const alreadyPaid = String(p.verificationStatus || '').toUpperCase() === 'PAID';
    const confirmedLink = hasConfirmedCashfreeLink(order);
    const wasAbandoned = String(p.verificationStatus || '').toUpperCase() === 'ABANDONED';
    // A live answer from Cashfree always outranks an older ABANDONED guess. When the guess
    // cannot be re-confirmed, drop it back to unverified instead of leaving a false verdict.
    const clearStaleAbandoned = wasAbandoned ? { verificationStatus: null } : {};

    if (!cashfreeOrder || cashfreeOrder.error) {
        if (alreadyPaid) return { result: RECON.NOOP, reason: 'already PAID; Cashfree order not returned' };
        const created = Date.parse(order?.createdAt || '') || null;
        // SAFETY: only call it ABANDONED when we are CERTAIN which Cashfree order this is.
        // With an inferred id, "not found" just means we looked up the wrong key — stamping
        // ABANDONED there marked genuinely-paid orders as abandoned.
        if (created && now - created > STALE_MS && confirmedLink) {
            return { result: RECON.ABANDONED, reason: 'no Cashfree order, stale', updates: { verificationStatus: 'ABANDONED', cfOrderStatus: 'NOT_FOUND', lastVerifiedAt: now } };
        }
        return {
            result: RECON.NOT_FOUND,
            reason: `Cashfree has no order "${cfId || cashfreeOrderId(order)}"${confirmedLink ? '' : ' (id derived from the ERP order number)'} — left unverified`,
            updates: { ...clearStaleAbandoned, cfOrderStatus: 'NOT_FOUND', lastVerifiedAt: now },
        };
    }

    const list = Array.isArray(payments) ? payments : [];
    const success = list.find((x) => x && x.payment_status === 'SUCCESS' && x.cf_payment_id);

    if (success) {
        const cur = String(success.payment_currency || cashfreeOrder.order_currency || '').toUpperCase();
        if (cur !== 'INR') return { result: RECON.NEEDS_REVIEW, reason: `non-INR currency: ${cur || 'unknown'}` };

        const paidPaise = rupeesToPaise(success.payment_amount);
        if (paidPaise === null) return { result: RECON.NEEDS_REVIEW, reason: 'unparseable paid amount' };
        if (paidPaise !== expectedPaise) {
            return { result: RECON.NEEDS_REVIEW, reason: `amount mismatch: paid ${paidPaise}p vs expected ${expectedPaise}p` };
        }
        if (String(cashfreeOrder.order_status).toUpperCase() !== 'PAID') {
            return { result: RECON.NEEDS_REVIEW, reason: `payment SUCCESS but order_status=${cashfreeOrder.order_status}` };
        }

        const priorId = p.cashfree_payment_id || p.cf_payment_id || null;
        if (alreadyPaid && priorId && priorId !== success.cf_payment_id) {
            return {
                result: RECON.ANOMALY,
                reason: `already PAID with ${priorId}; new success ${success.cf_payment_id}`,
                updates: { anomaly: { reason: 'conflicting_success_payment', priorId, newId: success.cf_payment_id, at: now } },
            };
        }
        if (alreadyPaid && priorId && priorId === success.cf_payment_id) {
            // Same payment, already PAID — but a PAID order WITHOUT its UTR is
            // unfinished bookkeeping. If Cashfree can hand us the audit trail now
            // (the UTR often lands moments after SUCCESS), top it up.
            const audit = extractPaymentAudit(success);
            if (!p.paymentAudit?.bank_reference && audit?.bank_reference) {
                return { result: RECON.PAID, cfPaymentId: success.cf_payment_id, reason: 'topping up the missing UTR audit', updates: { paymentAudit: audit, lastVerifiedAt: now } };
            }
            return { result: RECON.NOOP, reason: 'already PAID, same cf_payment_id' };
        }

        return {
            result: RECON.PAID,
            cfPaymentId: success.cf_payment_id,
            updates: {
                verificationStatus: 'PAID',
                gateway: 'Cashfree',
                sourceOfTruth: 'cashfree',
                mode: 'Prepaid',
                cashfree_payment_id: success.cf_payment_id,
                cf_status: 'SUCCESS',
                currency_verified: 'INR',
                amount_verified_paise: paidPaise,
                // Audit trail — UTR / UPI id / card / timing, straight from Cashfree.
                paymentAudit: extractPaymentAudit(success),
                lastVerifiedAt: now,
            },
        };
    }

    // No successful attempt. NEVER downgrade a previously-PAID order.
    if (alreadyPaid) return { result: RECON.NOOP, reason: 'already PAID; no new success attempt' };

    const os = String(cashfreeOrder.order_status || '').toUpperCase();

    if (list.some((x) => x && x.payment_status === 'FAILED')) {
        return { result: RECON.FAILED, reason: 'failed attempt', updates: { verificationStatus: 'FAILED', gateway: 'Cashfree', sourceOfTruth: 'cashfree', cfOrderStatus: os || 'UNKNOWN', lastVerifiedAt: now } };
    }
    if (list.some((x) => x && (x.payment_status === 'PENDING' || x.payment_status === 'USER_DROPPED'))) {
        return { result: RECON.PENDING, reason: 'pending/user_dropped', updates: { ...clearStaleAbandoned, cfOrderStatus: os || 'UNKNOWN', lastVerifiedAt: now } };
    }

    if (os === 'PAID') {
        // Order claims PAID but no verifiable SUCCESS attempt → cannot satisfy invariants.
        return { result: RECON.NEEDS_REVIEW, reason: 'order_status PAID but no verifiable payment attempt' };
    }
    if (os === 'ACTIVE') {
        // Cashfree says the payment link/order is still open. That is a REAL answer, not a
        // guess: the customer simply has not paid yet. Never call this abandoned.
        return {
            result: RECON.ACTIVE,
            reason: 'Cashfree order is ACTIVE — payment not completed yet',
            updates: { ...clearStaleAbandoned, cfOrderStatus: 'ACTIVE', lastVerifiedAt: now },
        };
    }
    const created = Date.parse(order?.createdAt || '') || null;
    if (created && now - created > STALE_MS && confirmedLink) {
        return { result: RECON.ABANDONED, reason: `no payment, stale (Cashfree order_status ${os || 'unknown'})`, updates: { verificationStatus: 'ABANDONED', cfOrderStatus: os || 'UNKNOWN', lastVerifiedAt: now } };
    }
    return { result: RECON.PENDING, reason: `awaiting payment (Cashfree order_status ${os || 'unknown'})`, updates: { ...clearStaleAbandoned, cfOrderStatus: os || 'UNKNOWN', lastVerifiedAt: now } };
    // NOTE: REFUNDED / REFUND_PENDING / chargeback are intentionally NOT mapped here.
    // Cashfree's /pg/orders/{id} and /payments responses do not carry refund/dispute
    // status — those require the separate Refunds/Settlements APIs, which are out of
    // D1A scope. We do not invent unsupported status mappings.
}
